import type {
  CraftifyV1EarnSocialAccount,
  ViduV1Activity,
  ViduV1Campion,
  ViduV1CreateEarnWithdrawRequest,
  ViduV1EarnApply,
  ViduV1EarnSubmit,
  ViduV1EarnWithdraw,
} from '@/app/apis';
import {
  activitiesSignupActivity,
  earnCreateWithdraw,
  earnGetActivity,
  earnGetMy,
  earnListActivities,
  earnListCampions,
  earnListMySubmits,
  earnSubmitApply,
  earnSubmitWork,
} from '@/app/apis';
import type { SupportedLanguage } from '@/app/types';
import { normalizeEarnActivityText } from '../_localized';

type ApiResponseErrorPayload = {
  message?: string;
  reason?: string;
  metadata?: Record<string, unknown>;
};

export class EarnApiError extends Error {
  reason?: string;
  metadata?: Record<string, unknown>;

  constructor(error: ApiResponseErrorPayload, fallbackMessage: string) {
    super(error.message || error.reason || fallbackMessage);
    this.name = 'EarnApiError';
    this.reason = error.reason;
    this.metadata = error.metadata;
  }
}

const assertNoApiError = (error: ApiResponseErrorPayload | undefined, fallbackMessage: string) => {
  if (!error) return;

  throw new EarnApiError(error, fallbackMessage);
};

export type EarnActivity = ViduV1Activity;
export type EarnWork = ViduV1EarnSubmit;
type EarnApplyStatus = Exclude<ViduV1EarnApply['status'], 'unspecified' | undefined> | 'not_applied';
export type EarnMyApply = NonNullable<Awaited<ReturnType<typeof earnGetMy>>['data']> & {
  status: EarnApplyStatus;
};

const campionToActivity = (campion: ViduV1Campion, locale: SupportedLanguage): EarnActivity => {
  const phase = campion.phase === 'unspecified' ? undefined : campion.phase;
  const activity: EarnActivity = {
    id: campion.activity_id ?? campion.id ?? '',
    title: normalizeEarnActivityText({ title: campion.title ?? '' } as EarnActivity, locale).title,
    content: normalizeEarnActivityText({ content: campion.content ?? '' } as EarnActivity, locale).content,
    phase,
    cover_uri: undefined,
    start_time: undefined,
    end_time: undefined,
  };

  return normalizeEarnActivityText(activity, locale);
};

export const listFeaturedActivities = async (locale: SupportedLanguage = 'en'): Promise<EarnActivity[]> => {
  const response = await earnListCampions();

  return (response.data?.campions ?? []).map((campion) => campionToActivity(campion, locale));
};

export const listActivityPage = async ({
  locale = 'en',
  pageSize,
  pageToken,
}: {
  locale?: SupportedLanguage;
  pageSize?: number;
  pageToken?: string;
} = {}): Promise<{ activities: EarnActivity[]; nextPageToken: string }> => {
  const response = await earnListActivities({
    query: {
      'pager.pagesz': pageSize,
      'pager.page_token': pageToken || undefined,
    },
  });

  const activities = (
    response.data?.activities
      ?.map((item) => item.activity)
      .filter((activity): activity is EarnActivity => Boolean(activity)) ?? []
  ).map((activity) => normalizeEarnActivityText(activity, locale));

  return {
    activities,
    nextPageToken: response.data?.next_page_token ?? '',
  };
};

export const listActivities = async (locale: SupportedLanguage = 'en'): Promise<EarnActivity[]> => {
  const page = await listActivityPage({ locale });

  return page.activities;
};

export const getActivityDetail = async (id: string, locale: SupportedLanguage = 'en'): Promise<EarnActivity | null> => {
  const response = await earnGetActivity({
    path: {
      id,
    },
  });

  return response.data?.activity ? normalizeEarnActivityText(response.data.activity, locale) : null;
};

const normalizeApplyStatus = (status: ViduV1EarnApply['status']): EarnApplyStatus => {
  if (status === 'approved' || status === 'reviewing' || status === 'rejected') {
    return status;
  }

  return 'not_applied';
};

export const normalizeMyApply = (data: Awaited<ReturnType<typeof earnGetMy>>['data']): EarnMyApply => ({
  ...data,
  status: normalizeApplyStatus(data?.apply?.status),
});

export const getMyApply = async (): Promise<EarnMyApply> => {
  const response = await earnGetMy();

  return normalizeMyApply(response.data);
};

export const getApplyStatus = async (): Promise<EarnApplyStatus> => {
  const myApply = await getMyApply();

  return myApply.status;
};

export const getMyStatus = async (
  locale: SupportedLanguage = 'en',
  myApply?: EarnMyApply,
  submitsPage?: {
    pageSize?: number;
    pageToken?: string;
  },
): Promise<{
  activities: EarnActivity[];
  status: EarnApplyStatus;
  worksNextPageToken: string;
  works: unknown[];
}> => {
  const [nextMyApply, activitiesResponse, submitsResponse] = await Promise.all([
    myApply ? Promise.resolve(myApply) : getMyApply(),
    earnListActivities({
      query: {
        signed_up: true,
      },
    }),
    earnListMySubmits({
      query: {
        'pager.pagesz': submitsPage?.pageSize,
        'pager.page_token': submitsPage?.pageToken,
      },
    }),
  ]);

  const activities =
    activitiesResponse.data?.activities
      ?.map((item) => item.activity)
      .filter((activity): activity is EarnActivity => Boolean(activity)) ?? [];

  return {
    ...nextMyApply,
    activities: activities.map((activity) => normalizeEarnActivityText(activity, locale)),
    works: submitsResponse.data?.submits ?? [],
    worksNextPageToken: submitsResponse.data?.next_page_token ?? '',
  };
};

type ApplyAccountInput = {
  channel?: string;
  platform?: string;
  url?: string;
  link?: string;
  screenshot?: string;
};

const normalizeApplyAccount = (account: ApplyAccountInput): CraftifyV1EarnSocialAccount => {
  const rawChannel = account.channel ?? account.platform;
  const channel =
    rawChannel === 'wechatVideo' || rawChannel === 'wechat_video' || rawChannel === 'wechat_channels'
      ? 'wechat'
      : rawChannel;

  return {
    channel: channel as CraftifyV1EarnSocialAccount['channel'],
    link: account.link ?? account.url,
    screenshot: account.screenshot,
  };
};

export const submitApply = async (accounts: ApplyAccountInput[] | unknown[]): Promise<unknown[]> => {
  const response = await earnSubmitApply({
    body: {
      accounts: (accounts as ApplyAccountInput[]).map(normalizeApplyAccount),
    },
  });

  assertNoApiError(response.error, 'Submission failed. Please try again later.');

  return accounts;
};

export const signupActivity = async (activity: EarnActivity): Promise<void> => {
  if (!activity.id) {
    throw new Error('Activity id is required.');
  }

  const response = await activitiesSignupActivity({
    body: {
      activity_id: activity.id,
    },
    path: {
      activity_id: activity.id,
    },
  });

  assertNoApiError(response.error, 'Sign-up failed. Please try again later.');
};

export const submitWork = async ({
  activity,
  workUrl,
}: {
  activity: EarnActivity;
  workUrl: string;
}): Promise<EarnWork | undefined> => {
  if (!activity.id) {
    throw new Error('Activity id is required.');
  }

  const response = await earnSubmitWork({
    body: {
      content: workUrl,
      target_id: activity.id,
    },
    path: {
      target_id: activity.id,
    },
  });

  assertNoApiError(response.error, 'Upload failed. Please try again later.');

  return response.data;
};

export const listMySubmits = async ({
  pageSize,
  pageToken,
}: {
  pageSize?: number;
  pageToken?: string;
} = {}): Promise<{ nextPageToken: string; submits: unknown[] }> => {
  const response = await earnListMySubmits({
    query: {
      'pager.pagesz': pageSize,
      'pager.page_token': pageToken,
    },
  });

  return {
    nextPageToken: response.data?.next_page_token ?? '',
    submits: response.data?.submits ?? [],
  };
};

export const createWithdraw = async ({
  type,
}: {
  type: Exclude<ViduV1CreateEarnWithdrawRequest['type'], 'unspecified'>;
}): Promise<ViduV1EarnWithdraw | undefined> => {
  const response = await earnCreateWithdraw({
    body: {
      type,
    },
  });

  assertNoApiError(response.error, 'Exchange request failed. Please try again later.');

  return response.data;
};
